import { Component, Suspense } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

import { FullPageLoadingSpinner } from '@acre/design-system'
import { Locales, messagesAlerts } from '@acre/utils'

import AuthErrorPage from './AuthErrorPage'

const LOGIN_REDIRECT_MESSAGE = messagesAlerts[Locales.GB].alerts.login

type BoundaryState = { error: Error | null }

class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    // A 401 triggers a redirect to /login — suppress AuthErrorPage during navigation
    if (error.message === LOGIN_REDIRECT_MESSAGE) return <FullPageLoadingSpinner />
    return <AuthErrorPage isDisabled={error.message?.includes('User is disabled')} />
  }
}

const AppSuspenseWrapper = ({ children }: { children: ReactNode }) => (
  <AppErrorBoundary>
    <Suspense fallback={<FullPageLoadingSpinner />}>{children}</Suspense>
  </AppErrorBoundary>
)

export default AppSuspenseWrapper
